import { traitsV1 } from '@equiview/protocol';
import { useState } from 'react';
import { formatarValor, unidadeCurta } from '../dados/catalogo.js';
import type { Animal, Avaliacao, Medida, OrigemDoValor } from '../dados/fonte.js';
import { BotaoLink, classeTd, classeTh, Etiqueta, Painel, Recolhivel, Tabela } from './ui/index.js';

/**
 * De onde veio cada valor. Toda ficha do banco e medida no campo e nao mostra
 * nada; so o animal real da demonstracao traz valores tirados da foto ou
 * estimados — e eles nunca aparecem sem a marca.
 */

const ROTULO: Record<OrigemDoValor, string> = { campo: 'medido', foto: 'foto', estimado: 'estimado' };
const TOM: Record<OrigemDoValor, string> = {
  campo: 'border-ok/40 text-ok',
  foto: 'border-transparent bg-destaque-fundo text-destaque-texto',
  estimado: 'border-dashed border-borda-forte text-apagado',
};

function Selo({ texto, classe, dica }: { texto: string; classe: string; dica?: string | undefined }) {
  return (
    <span
      title={dica}
      className={`ml-1.5 inline-flex h-4 items-center rounded-(--radius-etiqueta) border px-1 align-middle text-[10px] leading-none font-semibold tracking-wide uppercase ${classe}`}
    >
      {texto}
    </span>
  );
}

/** As marcas depois do valor: "foto", "estimado" e/ou "conferir". Medido no campo, sem nada. */
export function MarcaDeOrigem({ medidas }: { medidas: readonly Pick<Medida, 'origem' | 'como' | 'conferir'>[] }) {
  const origem = medidas.find((m) => m.origem && m.origem !== 'campo')?.origem;
  const conferir = medidas.find((m) => m.conferir)?.conferir;
  const como = medidas.find((m) => m.como)?.como;
  if (!origem && !conferir) return null;
  return (
    <>
      {origem && <Selo texto={ROTULO[origem]} classe={TOM[origem]} dica={como} />}
      {conferir && <Selo texto="conferir" classe="border-aviso/40 text-aviso" dica={conferir} />}
    </>
  );
}

/** Medida que a ficha v1.0 nao tem (o Rafael mediu a mais). */
export function MarcaForaDoProtocolo({ original }: { original?: string | undefined }) {
  return (
    <Selo
      texto="fora da v1.0"
      classe="border-marca/40 text-marca"
      dica={`Medida que a ficha v1.0 não tem${original ? `; na mensagem: "${original}"` : ''}. Candidata a uma versão 1.1 do protocolo.`}
    />
  );
}

const rotuloDoTrait = new Map(traitsV1.map((t) => [t.code, t]));

function valorComUnidade(m: Medida): string {
  const t = rotuloDoTrait.get(m.trait_code);
  return `${formatarValor(m.value_num, t)}${unidadeCurta(t)}`;
}

/**
 * Cartao da ficha de um animal real: quem coletou, quantos valores vieram de
 * cada origem, como cada um foi obtido, o que conferir e as medidas que a ficha
 * v1.0 nao tem. E o que um professor pergunta primeiro.
 */
export function PainelDadosReais({ animal, ficha, medidas }: { animal: Animal; ficha: Avaliacao; medidas: readonly Medida[] }) {
  const [abertos, setAbertos] = useState<ReadonlySet<string>>(new Set());
  const alternar = (k: string) =>
    setAbertos((a) => {
      const n = new Set(a);
      if (!n.delete(k)) n.add(k);
      return n;
    });
  const real = animal.real;
  if (!real) return null;

  const daFicha = medidas.filter((m) => m.assessment_id === ficha.id);
  const conta = (o: OrigemDoValor) => daFicha.filter((m) => (m.origem ?? 'campo') === o);
  const campo = conta('campo');
  const foto = conta('foto');
  const estimados = conta('estimado');
  const aprumos = estimados.filter((m) => rotuloDoTrait.get(m.trait_code)?.kind === 'aprumo');
  const estimadosSemAprumo = estimados.filter((m) => rotuloDoTrait.get(m.trait_code)?.kind !== 'aprumo');

  // Mesma mensagem para varias medidas (a GEO-001 envolve tres): uma linha so.
  const paraConferir = new Map<string, string[]>();
  for (const m of daFicha.filter((x) => x.conferir && x.origem !== 'estimado')) {
    const nome = rotuloDoTrait.get(m.trait_code)?.label ?? m.trait_code;
    paraConferir.set(m.conferir!, [...(paraConferir.get(m.conferir!) ?? []), nome]);
  }

  const comoObtido = [...foto, ...estimadosSemAprumo, ...campo.filter((m) => m.como)];

  return (
    <Painel
      titulo="Dados reais: de onde vem cada valor"
      acoes={<Etiqueta tom="ok">Coleta: {real.coleta}</Etiqueta>}
    >
      <div className="space-y-5">
        <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-(--radius-painel) border border-borda bg-borda sm:grid-cols-3 lg:grid-cols-5">
          {(
            [
              ['Medidos no animal', campo.length, 'pelo Rafael, com fita e hipômetro', 'campo'],
              ['Tirados da foto', foto.length, 'ângulos pela fotogrametria do app', 'foto'],
              ['Estimados', estimadosSemAprumo.length, 'por proporção, sem medida', 'estimado'],
              ['Aprumos estimados', aprumos.length, 'avaliação visual nas fotos', 'estimado'],
              ['Fora da ficha v1.0', real.extras.length, 'medidos pelo Rafael, junto das outras medidas', 'extra'],
            ] as const
          ).map(([rotulo, n, texto, origem]) => (
            <div key={rotulo} className="bg-superficie px-4 py-3">
              <dt className="text-xs text-apagado">
                {rotulo}
                {origem === 'extra' ? <MarcaForaDoProtocolo /> : <Selo texto={ROTULO[origem]} classe={TOM[origem]} />}
              </dt>
              <dd className="numero mt-1 text-2xl font-semibold text-texto">{n}</dd>
              <dd className="text-xs text-apagado">{texto}</dd>
            </div>
          ))}
        </dl>

        {paraConferir.size > 0 && (
          <div className="space-y-2">
            <h3 className="rotulo-secao">Para conferir com o Rafael</h3>
            <ul className="space-y-2 text-sm">
              {[...paraConferir].map(([motivo, nomes]) => (
                <li key={motivo} className="border-l-2 border-aviso px-3 py-1">
                  <span className="font-semibold">{nomes.join(', ')}:</span> {motivo}
                </li>
              ))}
            </ul>
          </div>
        )}

        <ul className="list-disc space-y-1 pl-5 text-sm text-apagado">
          {real.observacoes.map((o) => (
            <li key={o}>{o}</li>
          ))}
        </ul>

        {real.fotoDosAngulos && (
          <BotaoLink tamanho="sm" to={`/animais/${animal.id}/midia/${real.fotoDosAngulos}/angulos`}>
            Ver os ângulos marcados na foto
          </BotaoLink>
        )}

        <Recolhivel
          titulo="Como cada valor foi obtido"
          resumo={`${comoObtido.length} valores com explicação · aprumos: avaliação visual`}
          aberto={abertos.has('como')}
          onAlternar={() => alternar('como')}
        >
          <Tabela rotulo="Como cada valor foi obtido">
            <thead>
              <tr>
                <th scope="col" className={classeTh}>
                  Medida
                </th>
                <th scope="col" className={`${classeTh} text-right`}>
                  Valor
                </th>
                <th scope="col" className={classeTh}>
                  Como
                </th>
              </tr>
            </thead>
            <tbody>
              {comoObtido.map((m) => (
                <tr key={`${m.trait_code}-${m.side}`}>
                  <th scope="row" className={`${classeTd} text-left font-normal`}>
                    {rotuloDoTrait.get(m.trait_code)?.label ?? m.trait_code}
                  </th>
                  <td className={`${classeTd} numero text-right whitespace-nowrap`}>
                    {valorComUnidade(m)}
                    <MarcaDeOrigem medidas={[{ ...(m.origem ? { origem: m.origem } : {}) }]} />
                  </td>
                  <td className={`${classeTd} text-xs text-apagado`}>{m.como}</td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        </Recolhivel>
      </div>
    </Painel>
  );
}
